// src/pages/driver/cards/DeliverBinCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../services/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
  increment,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export default function DeliverBinCard({ job, company }) {
  const companyId = job?.companyId || company?.id || null;
  const companyName = job?.companyName || company?.name || "";

  const [customers, setCustomers] = useState([]);
  const [bins, setBins] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const [hasSaved, setHasSaved] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const fixedCustomerId = job?.customerId || "";
  const [customerId, setCustomerId] = useState(fixedCustomerId);
  const isFixedCustomer = !!fixedCustomerId;

  const [categoryId, setCategoryId] = useState(job?.binCategoryId || "");

  const [cart, setCart] = useState(() => {
    const map = {};
    (job?.deliveries || []).forEach((d) => {
      map[d.binId] = {
        id: d.binId,
        name: d.binName,
        qty: Number(d.qty || 0),
        categoryId: d.categoryId || "",
        categoryName: d.categoryName || "",
      };
    });
    return map;
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!companyId) {
        if (mounted) {
          setCustomers([]);
          setBins([]);
          setCategories([]);
        }
        return;
      }

      const [cs, bs, cats] = await Promise.all([
        getDocs(query(collection(db, "customers"), where("companyId", "==", companyId))),
        getDocs(query(collection(db, "bins"), where("companyId", "==", companyId))),
        getDocs(query(collection(db, "bin_categories"), where("companyId", "==", companyId))),
      ]);

      if (!mounted) return;

      const cList = cs.docs.map((d) => ({ id: d.id, ...d.data() }));
      const bList = bs.docs.map((d) => ({ id: d.id, ...d.data() }));
      const catList = cats.docs.map((d) => ({ id: d.id, ...d.data() }));

      cList.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      bList.sort((a, b) =>
        String(a.name || a.size || "").localeCompare(String(b.name || b.size || ""))
      );
      catList.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      setCustomers(cList);
      setBins(bList);
      setCategories(catList);
    })();

    return () => {
      mounted = false;
    };
  }, [companyId]);

  const cartArr = useMemo(
    () => Object.values(cart).filter((x) => Number(x.qty) > 0),
    [cart]
  );

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId]
  );

  const filteredBins = useMemo(() => {
    if (!categoryId) return [];
    return bins.filter((b) => String(b.categoryId || "") === String(categoryId));
  }, [bins, categoryId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) || null,
    [categories, categoryId]
  );

  function add(bin) {
    setCart((prev) => {
      const cur = prev[bin.id]?.qty || 0;
      return {
        ...prev,
        [bin.id]: {
          id: bin.id,
          name: bin.name,
          qty: cur + 1,
          categoryId: bin.categoryId || "",
          categoryName: bin.categoryName || "",
        },
      };
    });
  }

  function sub(binId) {
    setCart((prev) => {
      const cur = prev[binId]?.qty || 0;
      const next = Math.max(0, cur - 1);
      const copy = {
        ...prev,
        [binId]: { ...prev[binId], qty: next },
      };
      if (next === 0) delete copy[binId];
      return copy;
    });
  }

  function setQty(binId, q) {
    const n = Math.max(0, Number(q) || 0);
    setCart((prev) => {
      const existingBin = bins.find((b) => b.id === binId);
      const it = prev[binId] || {
        id: binId,
        name: existingBin?.name || "",
        qty: 0,
        categoryId: existingBin?.categoryId || "",
        categoryName: existingBin?.categoryName || "",
      };
      const copy = { ...prev, [binId]: { ...it, qty: n } };
      if (n === 0) delete copy[binId];
      return copy;
    });
  }

  function resetCart() {
    setCart({});
  }

  useEffect(() => {
    if (!categoryId) {
      setCart((prev) => {
        const next = {};
        return next;
      });
      return;
    }

    setCart((prev) => {
      const next = {};
      Object.values(prev).forEach((item) => {
        if (String(item.categoryId || "") === String(categoryId)) {
          next[item.id] = item;
        }
      });
      return next;
    });
  }, [categoryId]);

  async function save(status) {
    if (!customerId) {
      alert("Please select a customer.");
      return false;
    }
    if (!categoryId) {
      alert("Please select a bin category.");
      return false;
    }

    const totalSelected = cartArr.reduce((s, d) => s + Number(d.qty || 0), 0);
    const completing = status === "completed";

    if (completing && totalSelected <= 0) {
      alert("Add at least one bin before completing.");
      return false;
    }

    const currentMap = {};
    cartArr.forEach((x) => {
      currentMap[x.id] = {
        binId: x.id,
        binName: x.name,
        qty: Number(x.qty || 0),
        categoryId: x.categoryId || "",
        categoryName: x.categoryName || "",
      };
    });

    const prevSnap = await getDocs(
      query(
        collection(db, "bin_loans"),
        where("jobId", "==", job.id),
        where("direction", "==", "out")
      )
    );
    const prevDocs = prevSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const prevMap = {};
    prevDocs.forEach((d) => {
      const bid = String(d.binId || "");
      prevMap[bid] = (prevMap[bid] || 0) + Number(d.qty || 0);
    });

    const allBinIds = new Set([
      ...Object.keys(prevMap),
      ...Object.keys(currentMap),
    ]);

    const diffs = [];
    allBinIds.forEach((binId) => {
      const cur = currentMap[binId]?.qty || 0;
      const prev = prevMap[binId] || 0;
      const delta = cur - prev;
      if (delta !== 0) {
        const binDoc = bins.find((b) => b.id === binId);
        diffs.push({
          binId,
          binName: currentMap[binId]?.binName || binDoc?.name || "",
          categoryId: currentMap[binId]?.categoryId || binDoc?.categoryId || "",
          categoryName:
            currentMap[binId]?.categoryName || binDoc?.categoryName || "",
          delta,
        });
      }
    });

    const batch = writeBatch(db);

    const deliveries = cartArr.map((x) => ({
      binId: x.id,
      binName: x.name,
      qty: Number(x.qty || 0),
      categoryId: x.categoryId || "",
      categoryName: x.categoryName || "",
    }));

    batch.set(
      doc(db, "jobs", job.id),
      {
        status: status || job.status || "in_progress",
        companyId,
        companyName,
        customerId,
        customerName: selectedCustomer?.name || job.customerName || "",
        binCategoryId: categoryId,
        binCategoryName: selectedCategory?.name || "",
        deliveries,
        totalDelivered: deliveries.reduce((s, d) => s + Number(d.qty || 0), 0),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const cRef = doc(db, "customers", customerId);
    diffs.forEach((d) => {
      batch.set(
        cRef,
        {
          [`binHires.${d.binId}.name`]: d.binName,
          [`binHires.${d.binId}.count`]: increment(d.delta),
          totalHires: increment(d.delta),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    });

    allBinIds.forEach((binId) => {
      const binDoc = bins.find((b) => b.id === binId);
      const cur = currentMap[binId]?.qty || 0;
      const name = currentMap[binId]?.binName || binDoc?.name || "";
      const categoryIdValue =
        currentMap[binId]?.categoryId || binDoc?.categoryId || "";
      const categoryNameValue =
        currentMap[binId]?.categoryName || binDoc?.categoryName || "";
      const key = `${job.id}_${binId}_out`;

      batch.set(
        doc(db, "bin_loans", key),
        {
          _key: key,
          jobId: job.id,
          companyId,
          companyName,
          customerId,
          customerName: selectedCustomer?.name || "",
          binId,
          binName: name,
          categoryId: categoryIdValue,
          categoryName: categoryNameValue,
          direction: "out",
          qty: cur,
          updatedAt: serverTimestamp(),
          source: "deliverBinCard",
        },
        { merge: true }
      );
    });

    prevDocs.forEach((p) => {
      const expectedId = `${job.id}_${p.binId}_out`;
      if (p.id !== expectedId) {
        batch.delete(doc(db, "bin_loans", p.id));
      }
    });

    await batch.commit();
    setHasSaved(true);
    return true;
  }

  return (
    <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6 space-y-4">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
        Deliver bins
      </h3>
      <p className="text-sm text-gray-500">{companyName || "Greenspace"}</p>

      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="text-xs uppercase text-gray-500">Address</div>
        <div className="font-medium">{job?.address?.line1 || "-"}</div>
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-2">
        {isFixedCustomer ? (
          <div className="rounded-xl border px-3 py-2 bg-gray-50">
            <div className="text-xs uppercase text-gray-500">Customer</div>
            <div className="font-medium">
              {job?.customerName || "Assigned by Admin"}
            </div>
          </div>
        ) : (
          <label className="block">
            <div className="text-sm font-medium mb-1">Customer</div>
            <select
              className="border rounded px-3 py-2 w-full"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={!customers.length}
            >
              <option value="">Select customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {!customers.length && companyId && (
              <div className="text-xs text-amber-700 mt-1">
                No customers found for this company.
              </div>
            )}
          </label>
        )}

        <div className="flex items-end">
          <div className="text-sm text-gray-600">
            {selectedCustomer ? (
              <>
                Delivering to <b>{selectedCustomer.name}</b>
              </>
            ) : (
              "Choose a customer to begin."
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <div className="text-sm font-medium mb-1">Bin Category</div>
          <select
            className="border rounded px-3 py-2 w-full"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-end">
          <div className="text-sm text-gray-600">
            {selectedCategory ? (
              <>
                Selected category: <b>{selectedCategory.name}</b>
              </>
            ) : (
              "Choose a category to show bins."
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
        {!categoryId ? (
          <div className="col-span-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500 text-center">
            Select a bin category first.
          </div>
        ) : !filteredBins.length ? (
          <div className="col-span-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500 text-center">
            No bins found in this category.
          </div>
        ) : (
          filteredBins.map((b) => {
            const qty = cart[b.id]?.qty || 0;
            return (
              <button
                key={b.id}
                onClick={() =>
                  add({
                    id: b.id,
                    name: b.name || b.size || "Bin",
                    categoryId: b.categoryId || "",
                    categoryName: b.categoryName || "",
                  })
                }
                className="relative h-28 rounded-xl border grid place-items-center text-lg font-semibold"
              >
                <div className="text-center px-2">{b.name || b.size || "Bin"}</div>
                {qty > 0 && (
                  <span className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs rounded-full w-6 h-6 grid place-items-center">
                    {qty}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border p-3">
          <div className="flex items-center justify-between">
            <div className="font-medium">To Deliver</div>
            <div className="text-sm text-gray-600">
              Total: {cartArr.reduce((s, d) => s + Number(d.qty || 0), 0)}
            </div>
          </div>

          {!cartArr.length ? (
            <div className="text-gray-500 text-sm mt-2">No bins yet.</div>
          ) : (
            <ul className="divide-y mt-2">
              {cartArr.map((it) => (
                <li
                  key={it.id}
                  className="py-2 flex items-center justify-between gap-2"
                >
                  <div>
                    <div className="font-medium">{it.name}</div>
                    {it.categoryName && (
                      <div className="text-xs text-gray-500">{it.categoryName}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-2 rounded border"
                      onClick={() => sub(it.id)}
                    >
                      −
                    </button>
                    <input
                      className="w-14 border rounded px-2 py-1 text-center"
                      type="number"
                      min={0}
                      value={it.qty}
                      onChange={(e) => setQty(it.id, e.target.value)}
                    />
                    <button
                      className="px-2 rounded border"
                      onClick={() => add(it)}
                    >
                      +
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!!cartArr.length && (
            <div className="mt-3">
              <button className="px-3 py-1 rounded border" onClick={resetCart}>
                Reset
              </button>
            </div>
          )}
        </div>

        <div className="rounded-xl border p-3">
          <div className="font-medium mb-1">Notes</div>
          <div className="text-sm text-gray-600">
            Deliveries are now grouped by category and bin type, and the ledger also stores the selected category.
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition"
          onClick={async () => {
            await save("in_progress");
          }}
        >
          Save
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-sm font-medium text-white transition ${
            hasSaved
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-emerald-600/60 cursor-not-allowed"
          }`}
          disabled={!hasSaved || isCompleting}
          onClick={async () => {
            if (isCompleting) return;
            setIsCompleting(true);
            const ok = await save("completed");
            if (!ok) {
              setIsCompleting(false);
              return;
            }
            try {
              const _jobId =
                job?.id || job?.jobId || job?.jobID || job?.uid || job?._key || job?.key || "";
              if (_jobId) localStorage.setItem("autoFinishJobId", _jobId);
              try {
                window.dispatchEvent(new Event("autoFinishJob"));
              } catch {}
            } catch {}
            navigate("/driver", { replace: true });
          }}
        >
          {isCompleting ? "Completing..." : "Complete"}
        </button>
      </div>
    </div>
  );
}